import { NullableRecord, mapValues } from '@dozuki/web-js/utils';
import { StageFrequency } from '~/pages/api/courses/_shared/course-form/course-stage-editor/types';
import {
   Text,
   IconButton,
   Popover,
   PopoverTrigger,
   PopoverContent,
   PopoverArrow,
   PopoverCloseButton,
   PopoverHeader,
   PopoverBody,
   Select,
   Input,
   Stack,
} from '@chakra-ui/react';
import { useLocalization } from '~/framework';
import { ChangeEvent } from 'react';
import { Icon } from '~/ui-components';
import { faRotateForward } from '@fortawesome/pro-regular-svg-icons';
import { Stage } from '../Stage';

type StageFrequencyEditorProps = {
   stage: Stage;
   onChange: (newStageValues: Stage) => void;
};

export function StageFrequencyEditor({ stage, onChange }: StageFrequencyEditorProps) {
   const { _js } = useLocalization();

   const stageFrequencyDescriptions: NullableRecord<StageFrequency, string> = {
      null: _js('None'),
      day: _js('Days'),
      week: _js('Weeks'),
      month: _js('Months'),
      year: _js('Years'),
   };

   function handleUnitChange(e: ChangeEvent<HTMLSelectElement>) {
      onChange({
         ...stage,
         frequency_unit: e.target.value === 'null' ? null : (e.target.value as StageFrequency),
         frequency_quantity: e.target.value === 'null' ? null : 1,
      });
   }

   function handleQuantityChange(e: ChangeEvent<HTMLInputElement>) {
      onChange({
         ...stage,
         frequency_quantity: e.target.value === 'null' ? null : parseInt(e.target.value),
      });
   }

   return (
      <Popover>
         <PopoverTrigger>
            <IconButton
               aria-label={_js('Change Stage Recurrence')}
               icon={<Icon type={faRotateForward} />}
            />
         </PopoverTrigger>
         <PopoverContent>
            <PopoverArrow />
            <PopoverCloseButton />
            <PopoverHeader>{_js('Recurs')}</PopoverHeader>
            <PopoverBody>
               <Stack direction="row">
                  {stage.frequency_quantity !== null && (
                     <Input
                        flex={1}
                        type="number"
                        value={stage.frequency_quantity.toString()}
                        onChange={handleQuantityChange}
                     />
                  )}
                  <Select
                     flex={9}
                     value={stage.frequency_unit?.toString()}
                     onChange={handleUnitChange}
                  >
                     {mapValues(stageFrequencyDescriptions, (description, stageFrequency) => (
                        <option value={stageFrequency} key={stageFrequency}>
                           {description}
                        </option>
                     ))}
                  </Select>
               </Stack>
               <Text mt={2}>{_js('Set how often this stage should recur in the course.')}</Text>
            </PopoverBody>
         </PopoverContent>
      </Popover>
   );
}
